import {Component, Input, OnInit} from '@angular/core';
import {
  AppState,
  ContractType,
  Dictionary,
  financeStateId,
  MortgageContract,
  MortgageMonth,
  MortgageOverview,
  Scenario
} from '../../state/AppState';
import {
  AddMortgage,
  addMortgage,
  changeInflationRate,
  changeLogView,
  RemoveScenario,
  removeScenario,
  toggleInflationCorrection
} from '../../state/effects.actions';
import {select, Store} from '@ngrx/store';
import {map} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {toNumber} from '../../util/util';

export interface SeriesEntry {
  name: string;
  value: number;
}

export interface GraphLine {
  name: string;
  series: SeriesEntry[];
}

interface GraphData {
  principal: number;
  interest: number;
  expenses: number;
}

@Component({
  selector: 'mortgage-scenario',
  templateUrl: './scenario.component.html',
  styleUrls: ['./scenario.component.scss']
})
export class ScenarioComponent implements OnInit {
  title = 'scenario component';
  mortgagesLineData$: Observable<GraphLine[]>;
  mortgagePresent$: Observable<boolean>;
  showLog$: Observable<boolean>;
  useInflationCorrection$: Observable<boolean>;
  inflationRate$: Observable<number>;
  scenario$: Observable<Scenario>;
  mortgages$: Observable<Dictionary<MortgageOverview>>;
  _scenarioId: string;

  constructor(private store: Store<AppState>) {
  }

  @Input()
  set scenarioId(scenarioId: string) {
    this._scenarioId = scenarioId;
    console.log('set scenario');
    this.scenario$ = this.store.pipe(
      select((state) => {
        return state[financeStateId].scenarios[scenarioId];
      })
    );
    this.useInflationCorrection$ = this.scenario$.pipe(map(scenario => scenario?.useInflationCorrection));
    this.inflationRate$ = this.scenario$.pipe(map(scenario => scenario?.inflationRate));
    this.mortgages$ = this.scenario$.pipe(select(scenario => scenario?.mortgages));
    this.mortgagesLineData$ = this.scenario$.pipe(map(scenario => {
        if (scenario !== undefined) {
          return this.extractGraphline(scenario, scenario.useInflationCorrection, scenario.inflationRate)
        } else {
          return [] as GraphLine[];
        }
      }
    ));
    this.mortgagePresent$ = this.mortgagesLineData$.pipe(map(lineData => lineData[0]?.series?.length !== 0));
  }

  get scenarioId() {
    return this._scenarioId;
  }

  toNumber = toNumber


  ngOnInit(): void {
    console.log('Init Scenario component');

    this.showLog$ = this.store.pipe(map(state => state.config.logView));
  }

  submitUseInflationCorrection(value: boolean) {
    this.store.dispatch(toggleInflationCorrection({
      toggleInflationCorrection: value,
      scenarioId: this.scenarioId
    }));
  }

  submitChangeInflationRate(value: number) {
    this.store.dispatch(changeInflationRate({
      rate: value,
      scenarioId: this.scenarioId
    }));
  }

  extractGraphline(scenario: Scenario, useInflationCorrection: boolean, inflationRate: number): GraphLine[] {
    let accumulated: GraphData[] = [];

    let startDateEpoch = scenario.mortgages[0]?.contract.startDate;

    for (const i in scenario.mortgages) {
      const mortgage = scenario.mortgages[i];
      accumulated = this.accumulate(accumulated, mortgage.timeline);
      const dateEpoch = mortgage.contract.startDate;
      if (dateEpoch < startDateEpoch) {
        startDateEpoch = dateEpoch;
      }
    }

    if (useInflationCorrection) {
      accumulated = this.correctForInflation(accumulated, inflationRate);
    }

    let graphLines = [
      {
        name: 'principal',
        series: accumulated.map((data, i) => {
            return {
              name: this.determineYear(startDateEpoch, i),
              value: data.principal
            } as SeriesEntry;
          }
        )
      } as GraphLine,
      {
        name: 'interest',
        series: accumulated.map((data, i) => {
            return {
              name: this.determineYear(startDateEpoch, i),
              value: data.interest
            } as SeriesEntry;
          }
        )
      } as GraphLine,
      {
        name: 'monthly expense',
        series: accumulated.map((data, i) => {
            return {
              name: this.determineYear(startDateEpoch, i),
              value: data.expenses
            } as SeriesEntry;
          }
        )
      } as GraphLine
    ];
    console.log('recalculated');
    return graphLines;
  }

  private determineYear(startDate: number, index: number) {
    const newDate: Date = new Date(startDate);
    newDate.setMonth(newDate.getMonth() + index);
    const monthPart = newDate.getMonth() / 12;
    const years = newDate.getFullYear() + monthPart;
    const year = (newDate.getMonth() === 0) ? '' + years : years.toFixed(2);

    return year;
  }

  private correctForInflation(accumulated: GraphData[], inflationRate: number) {
    console.log('Correct for inflation')
    const corrected: GraphData[] = Object.assign([], accumulated);
    const inflationRatePct = inflationRate / 100;
    const inverseInflationRate = 1. / (1 + inflationRatePct) - 1;
    const monthlyInflationCorrection = Math.pow(1 + inverseInflationRate, 1 / 12);

    let currentCorrection = 1.;

    for (const i in corrected) {
      const current = corrected[i];

      current.expenses = current.expenses * currentCorrection;
      current.interest = current.interest * currentCorrection;
      current.principal = current.principal * currentCorrection;

      currentCorrection = currentCorrection * monthlyInflationCorrection;
    }
    return corrected;
  }

  private accumulate(accumulated: GraphData[], timeline: MortgageMonth[]): GraphData[] {
    const newData: GraphData[] = Object.assign([], accumulated);

    timeline.forEach((month, index) => {
        const current: GraphData = newData[index];
        if (current) {
          newData[index] = {
            principal: current.principal + month.newPrincipalPayment,
            interest: current.interest + month.newInterestPayment,
            expenses: current.expenses + month.newInterestPayment + month.newPrincipalPayment + month.newPremiumPayment
          } as GraphData;
        } else {
          newData[index] = {
            principal: month.newPrincipalPayment,
            interest: month.newInterestPayment,
            expenses: month.newInterestPayment + month.newPrincipalPayment + month.newPremiumPayment
          } as GraphData;
        }
      }
    );

    return newData;
  }

  addMortgage() {
    this.store.dispatch(addMortgage({
      scenarioId: this.scenarioId,
      contract: {
        mortgagedAmount: 220000,
        durationMonths: 240,
        interestRate: 2.01,
        monthlyPremium: 0,
        contractType: ContractType.LINEAR,
        startDate: new Date().getTime(), //FIXME: provide from field
      } as MortgageContract
    } as AddMortgage));
  }

  remove() {
    this.store.dispatch(removeScenario({
      scenarioId: this.scenarioId
    } as RemoveScenario));
  }

  submit(event: Event): void {
    event.preventDefault(); //no reload
  }


  submitShowLog(value: boolean) {
    this.store.dispatch(changeLogView({
      showLog: value
    }));
  }
}
